import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Phone, MapPin, Briefcase, Calendar, Users, Edit2, Lock, LogOut } from 'lucide-react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';

const ProfilePage = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [membershipData, setMembershipData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    phoneCountry: '+91', // Default to India
  });

  const countryCodes = [
    { code: '+91', country: 'India' },
    { code: '+1', country: 'USA' },
    { code: '+44', country: 'UK' },
    { code: '+61', country: 'Australia' },
    { code: '+65', country: 'Singapore' },
    { code: '+971', country: 'UAE' },
    { code: '+966', country: 'Saudi Arabia' },
    { code: '+60', country: 'Malaysia' },
    { code: '+86', country: 'China' },
    { code: '+81', country: 'Japan' },
  ];

  const calculateRemainingTime = (startDate) => {
    if (!startDate) return 'N/A';
    
    const start = new Date(startDate);
    const end = new Date(start);
    end.setFullYear(end.getFullYear() + 1); // Add one year
    
    const now = new Date();
    const diffTime = end - now;
    
    if (diffTime <= 0) return 'Expired';
    
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return `${diffDays} days remaining`;
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userValue = localStorage.getItem('user');
        if (!userValue) {
          navigate('/login');
          return;
        }

        const userData = JSON.parse(userValue);
        const userEmail = userData.value?.email;

        if (userEmail) {
          // Set user data from localStorage
          setUserData(userData.value);
          console.log("User data from localStorage:", userData.value);

          // Fetch membership details using the correct API endpoint
          const membershipResponse = await axios.post('https://atabackend.quindl.com/membership/get', {
            email: userEmail
          });
          
          if (membershipResponse.data?.membership && membershipResponse.data.membership.length > 0) {
            // Get the most recent membership
            const latestMembership = membershipResponse.data.membership[0];
            setMembershipData(latestMembership);
            console.log("Membership data fetched:", latestMembership);
          } else {
            setMembershipData(null);
          }

          setFormData({
            firstName: userData.value.firstName || '',
            lastName: userData.value.lastName || '',
            email: userData.value.email || '',
            phone: userData.value.phone || '',
            phoneCountry: userData.value.phoneCountry || '+91',
          });
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        // setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  const handleLogout = () => {
    console.log("Logging out user");
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    toast.success('Logged out successfully');
    navigate('/login');
    window.location.reload();
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    console.log("Password change requested:", passwordData);
    
    // Validate password match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }

    try {
      const userValue = localStorage.getItem('user');
      if (!userValue) {
        navigate('/login');
        return;
      }

      const userData = JSON.parse(userValue);
      const userEmail = userData.value?.email;
      
      const response = await axios.post('https://atabackend.quindl.com/auth/change-password', {
        email: userEmail,
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      if (response.data.message === 'Password updated successfully!') {
        setTimeout(() => {
          toast.success('Password changed successfully', { autoClose: 1000 });
        }, 1000);
        setShowPasswordChange(false);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        window.location.reload();
      }
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.message || 'Failed to change password');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const userString = localStorage.getItem('user');
      const user = JSON.parse(userString);
      const email = user.value.email;

      const response = await axios.post('https://atabackend.quindl.com/user/update', {
        email,
        ...formData
      });

      if (response.data) {
        // Update local storage
        const updatedUser = {
          ...user,
          value: {
            ...user.value,
            ...formData
          }
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        setUserData(updatedUser.value);
        setIsEditing(false);
        toast.success('Profile updated successfully');
      }
    } catch (err) {
      setError(err.message || 'Failed to update profile');
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#E16811]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full p-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="text-center">
              <div className="text-red-500 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load profile data</h3>
              <p className="text-gray-500 mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="bg-orange-500 text-white px-4 py-2 rounded-md hover:bg-orange-600 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <ToastContainer />
      <div className="max-w-7xl mx-auto">
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
          {/* Profile Header */}
          <div className="relative h-48 bg-gradient-to-r from-[#E16811] to-orange-500">
            <div className="absolute -bottom-16 left-8">
              <div className="h-32 w-32 rounded-full border-4 border-white bg-white flex items-center justify-center">
                <User className="h-20 w-20 text-[#E16811]" />
              </div>
            </div>
            <div className="absolute top-4 right-4 flex space-x-2">
              {/* <button
                onClick={() => setIsEditing(!isEditing)}
                className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                title="Edit Profile"
              >
                <Edit2 className="h-5 w-5 text-white" />
              </button> */}
              <button
                onClick={handleLogout}
                className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                title="Logout"
              >
                <LogOut className="h-5 w-5 text-white" />
              </button>
            </div>
          </div>

          {/* Profile Content */}
          <div className="pt-20 px-8 pb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Personal Information */}
              <div className="space-y-6">
                <h4 className="text-xl font-bold text-gray-900">Personal Information</h4>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="p-3 rounded-lg bg-[#E16811]/10">
                      <User className="w-6 h-6 text-[#E16811]" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Name</p>
                      <p className="font-medium text-gray-900">{userData?.firstName} {userData?.lastName}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="p-3 rounded-lg bg-[#E16811]/10">
                      <Mail className="w-6 h-6 text-[#E16811]" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium text-gray-900">{userData?.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="p-3 rounded-lg bg-[#E16811]/10">
                      <Phone className="w-6 h-6 text-[#E16811]" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      <p className="font-medium text-gray-900">
                        {userData?.phone ? `+${userData.phone.substring(0, 2)} ${userData.phone.substring(2)}` : ''}
                      </p>
                    </div>
                  </div>

                  {/* <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="p-3 rounded-lg bg-[#E16811]/10">
                      <MapPin className="w-6 h-6 text-[#E16811]" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Address</p>
                      <p className="font-medium text-gray-900">
                        {userData?.address}
                      </p>
                    </div>
                  </div> */}
                </div>
              </div>

              {/* Additional Information */}
              {/* <div className="space-y-6">
                <h4 className="text-xl font-bold text-gray-900">Additional Information</h4>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="p-3 rounded-lg bg-[#E16811]/10">
                      <Briefcase className="w-6 h-6 text-[#E16811]" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Profession</p>
                      <p className="font-medium text-gray-900">{userData?.profession}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="p-3 rounded-lg bg-[#E16811]/10">
                      <Calendar className="w-6 h-6 text-[#E16811]" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Date of Birth</p>
                      <p className="font-medium text-gray-900">{userData?.dateOfBirth}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="p-3 rounded-lg bg-[#E16811]/10">
                      <Users className="w-6 h-6 text-[#E16811]" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Marital Status</p>
                      <p className="font-medium text-gray-900">{userData?.maritalStatus}</p>
                    </div>
                  </div>
                </div>
              </div> */}
            </div>

            {/* Membership Details Section */}
            <div className="mt-8">
              <h4 className="text-xl font-bold text-gray-900 mb-6">Membership Details</h4>
              {!membershipData ? (
                <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 text-center">
                  <div className="flex flex-col items-center justify-center space-y-4">
                    <div className="p-4 rounded-full bg-gray-100">
                      <Users className="w-8 h-8 text-gray-400" />
                    </div>
                    <h5 className="text-lg font-semibold text-gray-900">No Active Memberships</h5>
                    <p className="text-gray-500">You don't have any active memberships at the moment.</p>
                    <button
                      onClick={() => navigate('/membership')}
                      className="mt-4 inline-flex items-center px-6 py-3 bg-[#E16811] text-white rounded-lg hover:bg-[#d15700] transition-colors"
                    >
                      Become a Member
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <h5 className="text-lg font-semibold text-[#E16811] mb-4">Current Membership</h5>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Membership Type:</span>
                        <span className="font-medium text-gray-900">
                          {membershipData?.membershipName}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Status:</span>
                        <span className={`font-medium ${
                          membershipData?.status === 'Active' 
                            ? 'text-green-600' 
                            : membershipData?.status === 'Pending'
                            ? 'text-yellow-600'
                            : 'text-red-600'
                        }`}>
                          {membershipData?.status}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Request ID:</span>
                        <span className="font-medium text-gray-900">
                          {membershipData?.requestId}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Submission Date:</span>
                        <span className="font-medium text-gray-900">
                          {membershipData?.createdAt 
                            ? new Date(membershipData.createdAt).toLocaleDateString() 
                            : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Membership Validity:</span>
                        <span className="font-medium text-gray-900">
                          {calculateRemainingTime(membershipData?.createdAt)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Date of Birth:</span>
                        <span className="font-medium text-gray-900">
                          {membershipData?.doB 
                            ? new Date(membershipData.doB).toLocaleDateString() 
                            : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <h5 className="text-lg font-semibold text-[#E16811] mb-4">Personal Details</h5>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Full Name:</span>
                        <span className="font-medium text-gray-900">
                          {membershipData?.firstname} {membershipData?.lastname}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Email:</span>
                        <span className="font-medium text-gray-900">
                          {membershipData?.email}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Phone:</span>
                        <span className="font-medium text-gray-900">
                          {membershipData?.phone}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Address:</span>
                        <span className="font-medium text-gray-900">
                          {membershipData?.address}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Profession:</span>
                        <span className="font-medium text-gray-900">
                          {membershipData?.profession}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {membershipData?.status === 'Pending' && (
                <div className="mt-6">
                  <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-yellow-400"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">
                          Under Review
                        </h3>
                        <p className="mt-1 text-sm text-yellow-700">
                          Your membership request is currently under review. We'll notify you once it's approved.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Password Change Section */}
            <div className="mt-8">
              <div className="flex justify-between items-center">
                <h4 className="text-xl font-bold text-gray-900">Security Settings</h4>
                <button 
                  onClick={() => setShowPasswordChange(!showPasswordChange)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#E16811] text-white rounded-lg hover:bg-[#d15700] transition-colors"
                >
                  <Lock className="w-4 h-4" />
                  {showPasswordChange ? 'Cancel' : 'Change Password'}
                </button>
              </div>

              {showPasswordChange && (
                <div className="mt-4 p-6 bg-gray-50 rounded-xl">
                  <form onSubmit={handlePasswordSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                        Current Password
                      </label>
                      <input
                        type="password"
                        id="currentPassword"
                        name="currentPassword"
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#E16811] focus:ring focus:ring-[#E16811] focus:ring-opacity-50"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                        New Password
                      </label>
                      <input
                        type="password"
                        id="newPassword"
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#E16811] focus:ring focus:ring-[#E16811] focus:ring-opacity-50"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        id="confirmPassword"
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#E16811] focus:ring focus:ring-[#E16811] focus:ring-opacity-50"
                        required
                      />
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        className="px-4 py-2 bg-[#E16811] text-white rounded-lg hover:bg-[#d15700] transition-colors"
                      >
                        Update Password
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>

            {/* Logout Button Bottom */}
            <div className="mt-8 flex justify-end">
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                <LogOut className="w-5 h-5" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;